import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { registerServiceWorker } from "@/lib/swRegister";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

// Global recovery: if a lazy-loaded route chunk fails to resolve (typical
// symptom of a stale Service Worker after a redeploy with new bundle
// hashes), nuke client caches + hard reload once. Doing this at the window
// level catches the case where the failure happens BEFORE React renders the
// Suspense fallback, so <ErrorBoundary> never sees it.
const CHUNK_REGEX = /(Loading chunk|ChunkLoadError|Cannot find module|Failed to fetch dynamically imported module|\.chunk\.js)/i;
const CHUNK_RECOVERY_KEY = "__xyz_chunk_recovered__";

async function recoverFromStaleChunks() {
  try {
    if (sessionStorage.getItem(CHUNK_RECOVERY_KEY)) return; // already tried once
    sessionStorage.setItem(CHUNK_RECOVERY_KEY, String(Date.now()));
    if (navigator?.serviceWorker) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
    }
    if (typeof caches !== "undefined") {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n).catch(() => {})));
    }
  } catch { /* noop */ }
  window.location.reload();
}

window.addEventListener("error", (e) => {
  const msg = e?.error?.message || e?.message || "";
  if (CHUNK_REGEX.test(msg)) recoverFromStaleChunks();
});
window.addEventListener("unhandledrejection", (e) => {
  const msg = e?.reason?.message || String(e?.reason || "");
  if (CHUNK_REGEX.test(msg)) recoverFromStaleChunks();
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// Boot the service worker AFTER React has rendered so it never competes with
// LCP on first paint. Subsequent visits will then serve hero img + main JS
// from the SW cache in <5ms.
registerServiceWorker();
